import React, { useCallback, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { hideError } from "store/reducers/errorReducer";
import { Amplify, API } from "aws-amplify";
import { withAuthenticator } from "auth/withAuthenticator";
import "@aws-amplify/ui-react/styles.css";
import * as FP from "utils/fp-js";
import awsconfig from "aws-config";
import { NoRole } from "routes/NoRole";
import { SuperAdmin } from "routes/SuperAdmin";
import { useConfirmationsWithCallback } from "hooks/useConfirmationWithCallback";
import { useWarnings } from "hooks/useWarnings";
import { Admin } from "routes/Admin";
import { useIsDbAwake } from "hooks/useIsDbAwake";
import { User } from "routes/User";
import { Marketer } from "routes/Marketer";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

Amplify.configure(awsconfig);

const App = withAuthenticator(({ signOut, user, handleAuthError }) => {
  const dispatch = useAppDispatch();
  const initialUserData = {};
  const [userData, setUserData] = useState(initialUserData);
  const [senderData, setSenderData] = useState(initialUserData);
  const confirmationWithCallbackProps = useConfirmationsWithCallback();
  const warningProps = useWarnings();
  const { isDbAwake } = useIsDbAwake();

  const [isGreetingsRead, setIsGreetingsRead] = useState(true);

  const loadSenderData = useCallback(async () => {
    try {
      const result = await API.get("restApi", `/users/get_sender`, {});
      setSenderData(result);
    } catch (err) {
      handleAuthError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserData = useCallback(
    async (cognitoId) => {
      let result = {};
      try {
        result = await API.get("restApi", `/users/${cognitoId}`, {});
      } catch (err) {
        handleAuthError(err);
      } finally {
        const {
          email,
          role = user?.attributes?.email === "admin@referral-stream.com"
            ? "Super Admin"
            : "No role",
          organizationId,
          organization,
          firstName,
          lastName,
        } = result;
        setUserData({
          cognitoId: user?.username,
          email: email ?? user.attributes.email,
          role,
          fullName: firstName && lastName && `${firstName} ${lastName}`,
          organizationId,
          organization,
          locations: result?.locations ?? [],
        });
        if (email) {
          loadSenderData();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.attributes?.email, user?.username]
  );

  useEffect(() => {
    dispatch(hideError());

    if (!isDbAwake) return;

    // @ts-ignore
    if (user?.username) {
      loadUserData(user?.username);
    }

    // @ts-ignore
    if (!user?.username) {
      setUserData({});
    }

    return () => {
      setUserData({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUserData, isDbAwake]);

  useEffect(() => {
    if (userData?.role) {
      const isGreetingsRead =
        window.localStorage.getItem(
          `${userData?.cognitoId}-is-greetings-read`
        ) === "true";
      setIsGreetingsRead(isGreetingsRead);
    }
  }, [userData]);

  return (
    <Elements stripe={stripePromise}>
      <GlobalContext.Provider
        value={{
          ...confirmationWithCallbackProps,
          ...warningProps,
          userData,
          loadUserData,
          senderData,
          loadSenderData,
          isGreetingsRead,
          setIsGreetingsRead,
        }}
      >
        {FP.match(
          // @ts-ignore
          userData?.role
        )
          .on("Super Admin", <SuperAdmin />)
          .on("Admin", <Admin />)
          .on("Scheduler", <User />)
          .on("Marketer", <Marketer />)
          .otherwise(<NoRole />)}
      </GlobalContext.Provider>
    </Elements>
  );
});

export default App;
